import React, { useMemo } from 'react';

import SvgSprite from '@/components/atomic/atoms/data-display/icons/icon-sprite';
import Image from '@/components/atomic/atoms/layout/image/image';
import type { ViewProps } from '@/components/atomic/atoms/layout/view/typings';
import Styled from '@/components/atomic/atoms/navigation/cta/styled';
import useIsIconValid from '@/hooks/use-is-icon-valid';
import type { Cta as ICta } from '@/typings/generic';
import normalisePath from '@/utils/path-helper';

const Cta: React.FC<
	ICta & {
		$block?: boolean;
		onClick?: (e?: React.MouseEvent) => void;
		tabIndex?: number;
		$pictureHeight?: number | string;
		useDynamicMedia?: boolean;
	} & ViewProps
> = ({
	ariaLabel,
	icon,
	image,
	link,
	linkType,
	target,
	text,
	$block,
	onClick,
	tabIndex = 0,
	$pictureHeight,
	useDynamicMedia = true,

	...props
}) => {
	const isIconValid = useIsIconValid(icon);
	const IconComponent = useMemo(() => {
		if (isIconValid) {
			return <SvgSprite icon={icon} />;
		}
		return null;
	}, [icon, isIconValid]);

	const ImageComponent = useMemo(() => {
		if (image?.src) {
			return (
				<Image
					src={image.src}
					alt={image.alt}
					$width={image.width ?? 16}
					$height={image.height ?? 16}
					$objectFit='contain'
					$pictureHeight={$pictureHeight}
					useDynamicMedia={useDynamicMedia}
				/>
			);
		}
		return null;
	}, [image]);

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (['Enter', 'Space'].includes(e.code)) {
			onClick?.();
		}
	};

	return (
		<Styled
			aria-label={ariaLabel}
			title={ariaLabel}
			target={target}
			$linkType={linkType}
			$hasIcon={Boolean(IconComponent)}
			$block={$block}
			onClick={onClick}
			icon={icon}
			tabIndex={tabIndex}
			onKeyDown={onKeyDown}
			{...props}
			href={normalisePath((props as { href: string }).href ?? link)}
		>
			{ImageComponent}
			{IconComponent}
			{text ? <span>{text}</span> : null}
		</Styled>
	);
};

export default Cta;
